/**
 * Persistent auto-order nudge — the small card pinned above the first day
 * card in every week column of the family order view. Three states, driven
 * by `useAutoOrderCoverage`:
 *
 *   - Nobody covered: a warm "turn on auto-order" invitation → the wizard.
 *   - Everybody covered: a one-line "auto-order is on · Manage" confirmation.
 *   - Mixed: an amber warning naming the kids whose meals are NOT placed
 *     automatically (they could miss order days) → turn-on CTA.
 *
 * Renders nothing while subscriptions load (no flash), for locations with no
 * children, and — per `resolveFamilyWeekBannerSlot` — whenever the missing-info
 * banner or the auto-reload funding alert owns the banner slot. The funding
 * alert case matters most in the `all` state: "Auto-order is on — meals are
 * placed automatically each week" is a lie while auto-reload is disarmed and
 * the runner is skipping the fill. The missing-info case is about having ONE
 * next step: a parent who can't order yet shouldn't be offered a second
 * "Set up" button competing with "Finish setup".
 */

import { Button } from '@ordo/ui/button'
import { Sparkles, TriangleAlert } from 'lucide-react'
import type { ReactElement } from 'react'
import { Link } from 'react-router'

import { autoOrderTargetHref, formatNameList, useAutoOrderCoverage } from './auto-order-coverage'
import type { FamilyOrderLocation } from './columns'
import { useFamilyWeekBanner } from './family-week-banner'

export function AutoOrderNudge({ location }: { location: FamilyOrderLocation }): ReactElement | null {
  const coverage = useAutoOrderCoverage(location.children, location.id)
  // Same derivation the alert asks — whoever loses the slot renders nothing.
  const { slot } = useFamilyWeekBanner()
  if (slot !== 'auto-order-nudge') {
    return null
  }
  if (!coverage || location.children.length === 0) {
    return null
  }

  if (coverage.state === 'all') {
    const manageHref = autoOrderTargetHref(coverage.covered, coverage.subByUserId)
    return (
      <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 dark:border-emerald-900 dark:bg-emerald-950/40">
        <Sparkles className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-500" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-sm text-emerald-800 dark:text-emerald-300">
          Auto-order is on — meals are placed automatically each week.
        </span>
        <Link
          to={manageHref}
          className="shrink-0 text-sm font-medium text-emerald-700 underline-offset-4 hover:underline dark:text-emerald-400">
          Manage
        </Link>
      </div>
    )
  }

  if (coverage.state === 'mixed') {
    const names = formatNameList(coverage.uncovered.map((c) => c.displayName))
    const verb = coverage.uncovered.length === 1 ? 'doesn’t' : 'don’t'
    return (
      <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900 dark:bg-amber-950/40">
        <TriangleAlert className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-500" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
            {names} {verb} have auto-order on
          </p>
          <p className="text-xs text-amber-800/90 dark:text-amber-300/90">
            Their meals aren&apos;t placed automatically, so they could miss days you forget to order.
          </p>
        </div>
        <Button asChild size="sm" variant="outline" className="shrink-0">
          <Link to={autoOrderTargetHref(coverage.uncovered, coverage.subByUserId)}>Turn on</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10">
        <Sparkles className="h-5 w-5 text-primary" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">Turn on auto-order</p>
        <p className="text-xs text-muted-foreground">
          We&apos;ll place meals your kids love every week. Skip days or pause anytime.
        </p>
      </div>
      <Button asChild size="sm" className="shrink-0">
        <Link to={autoOrderTargetHref(coverage.uncovered, coverage.subByUserId)}>Set up</Link>
      </Button>
    </div>
  )
}
